import { useState, useEffect } from "react";
import { Col, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/slices/auth";
import { postSearchIndex, getFormData, getFormVariables, getRecordSummary, getMasterAiSummary, getMasterSummaryReport, getPrintableTemplates, getTemplateFilters, getTemplateRecords, deleteRecord, deleteRecords } from "./apiController";
import { ColumnFilter } from "../../widgets/table/columnfilter";
import { FIELD_TYPES, FieldItem } from "../../widgets/fields";
import { convertToIST } from "../../utils/ConvertUTCToLocale";
import { CustomButton, showToaster, toasterTypes } from "../../widgets";
import ModalPopup from "../../widgets/modal";
import classnames from "classnames";
import _, { cloneDeep, filter, set } from "lodash";
import SurveyFormComponent from "../surveyForm/SurveyFormComponent";
import ViewForm from "./ViewForm";
import Dump from "./Dump";
import { ArrowLeftOutlined } from "@ant-design/icons";
import ViewRecordSummary from "./ViewRecordSummary";
import { agentNamesDataMap } from "../common/utils/aiAgentsData";
import ModalComponent from "../common/Modal/modal";
import ReactMarkdown from 'react-markdown';
import { multiRecordReportElements, printableTemplateElements } from "./FormElements";
import useFieldItem from "../../widgets/fields/UseFieldItem";
import GenerateReport from "./GenerateReport";
import dayjs from "dayjs";

type SelectionMode = "single" | "multiAcrossForms" | "multiWithinForms";

interface TabDataItem {
    formName: string;
    formId: string;
    headers: any;
    bodydata: any[];
    formIndex: number;
    filterData: any[];
    originalFormData?: any;
}

interface TabStatesItem {
    showDetailedView: boolean;
    detailedViewData?: {
        form: any;
        formName: string;
        formKey: string;
        recordIndex: number;
    } | null;
}

interface SearchIndexTabsProps {
    existingData: any[];
    getExistingData: (targetFormData: any, autoFillFields?: any) => void;
    autoFillMultiRecord?: (targetFormData: any, autoFillFieldsArray: any[]) => void;
    topicId: string;
    searchIndexUidData: any[];
    onClose: () => void;
    renderDataEntryForm: any;
    renderType: string;
    projectData?: any;
    forms?: any[];
    formData?: any;
    selectedIndexData?: any;
    permissionData?: any;
    onRefresh?: () => void;
}

const SearchIndexTabs: React.FC<SearchIndexTabsProps> = ({
    existingData,
    getExistingData,
    autoFillMultiRecord,
    topicId,
    searchIndexUidData,
    onClose,
    renderDataEntryForm,
    renderType,
    projectData = null,
    forms = null,
    formData = null,
    selectedIndexData = null,
    permissionData = null,
    onRefresh
}) => {
    const [activeTab, setActiveTab] = useState<string>("data-entry");
    const [tabData, setTabData] = useState<Record<string, TabDataItem>>({});

    const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState<boolean>(false);
    const [selectedSingleRecord, setSelectedSingleRecord] = useState<string | null>(null);
    const [selectedMultiAcrossForms, setSelectedMultiAcrossForms] = useState<Record<string, string>>({});
    const [selectedMultiWithinForms, setSelectedMultiWithinForms] = useState<Record<string, number[]>>({});

    const [tabStates, setTabStates] = useState<Record<string, TabStatesItem>>({});
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    const [action, setAction] = useState<string>("");
    const [masterUidData, setMasterUidData] = useState<any>(null);
    const [customNavigationState, setCustomNavigationState] = useState<any>(null);
    const [viewFormData, setViewFormData] = useState<any>(null);
    const [viewFormVariables, setViewFormVariables] = useState<any>(null);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);
    const [selectedFormData, setSelectedFormData] = useState<any>(null);
    const [currentFormKey, setCurrentFormKey] = useState<string>("");
    const [currentRecordIndex, setCurrentRecordIndex] = useState<number>(0);
    const [masterData, setmasterData] = useState<any>(null);
    const [selectedTargetForm, setSelectedTargetForm] = useState<any>("");
    const [multiRecordsSummaryReportData, setMultiRecordsSummaryReportData] = useState<any>(null);
    const [multiRecordReportType, setMultiRecordReportType] = useState("aiSummary");
    const [multiRecordSummaryPrompt, setMultiRecordSummaryPrompt] = useState("");
    const [printableTemplate, setPrintableTemplate] = useState<any>(null);
    const [printableTemplateValues, setPrintableTemplateValues] = useState([]);
    const [templateData, setTemplateData] = useState<any>([]);
    const [selectedTemplateData, setSelectedTemplateData] = useState<any>(null);
    const [templateFilters, setTemplateFilters] = useState<any[]>([]);
    const [filterValues, setFilterValues] = useState<Record<string, any>>({});
    const [reportData, setReportData] = useState<any>([]);

    const gridFields = ["checkBoxGrid", "radioButtonGrid", "textBoxGrid"];

    const [values, fields, handleChange, { validateValues, reset, updateValue, updateError, updateAllError, removeErrors }] = useFieldItem({}, {}, { onValueChange });

    const dispatch = useDispatch();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const selectionMode: SelectionMode = (() => {
        const hasMultiWithin = Object.values(selectedMultiWithinForms).some(arr => arr.length > 0);

        if (hasMultiWithin) return "multiWithinForms";

        if (Object.keys(selectedMultiAcrossForms).length > 1) return "multiAcrossForms";

        if (selectedSingleRecord) return "single";

        return "single";
    })();

    useEffect(() => {
        if (isMultiSelectEnabled) {
            setSelectedSingleRecord(null);
            setTabStates(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(key => {
                    updated[key] = {
                        ...updated[key],
                        showDetailedView: false
                    };
                });
                return updated;
            });
        } else {
            setSelectedMultiWithinForms({});
            setTabStates(prev => {
                const updated = { ...prev };
                const newMultiAcross = { ...selectedMultiAcrossForms };
                Object.keys(tabData).forEach(formKey => {
                    if (tabData[formKey].bodydata?.length === 1) {
                        const form = tabData[formKey].bodydata[0];
                        updated[formKey] = {
                            showDetailedView: true,
                            detailedViewData: {
                                form,
                                formName: tabData[formKey].formName,
                                formKey,
                                recordIndex: 0
                            }
                        };
                        newMultiAcross[formKey] = `${formKey}_0`;
                        // Set single record selection for the active tab
                        if (formKey === activeTab) {
                            setSelectedSingleRecord(`${formKey}_0`);
                        }
                    }
                });
                setSelectedMultiAcrossForms(newMultiAcross);
                return updated;
            });
        }
    }, [isMultiSelectEnabled, tabData, activeTab]);

    useEffect(() => {
        if (existingData && existingData.length > 0) {
            initializeTabs();
        }
    }, [existingData]);

    useEffect(() => {
        if (activeTab && tabStates[activeTab]?.showDetailedView && tabData[activeTab]?.bodydata?.length === 1) {
            if (!isMultiSelectEnabled) {
                setSelectedSingleRecord(`${activeTab}_0`);
            }
        }
    }, [activeTab, tabStates, isMultiSelectEnabled]);

    useEffect(() => {
        if (tabData && Object.keys(tabData).length > 0 && isInitialLoad) {
            const formKeys = Object.keys(tabData);
            const newTabStates: Record<string, TabStatesItem> = {};
            const initialMultiAcross: Record<string, string> = {};

            for (const formKey of formKeys) {
                if (tabData[formKey].bodydata?.length === 1 && !isMultiSelectEnabled) {
                    newTabStates[formKey] = {
                        showDetailedView: true,
                        detailedViewData: {
                            form: tabData[formKey].bodydata[0],
                            formName: tabData[formKey].formName,
                            formKey,
                            recordIndex: 0
                        }
                    };
                    initialMultiAcross[formKey] = `${formKey}_0`;
                    if (formKey === formKeys[0] && !isMultiSelectEnabled) {
                        setSelectedSingleRecord(`${formKey}_0`);
                    }
                } else {
                    newTabStates[formKey] = {
                        showDetailedView: false,
                        detailedViewData: null
                    };
                }
            }

            setTabStates(newTabStates);
            setSelectedMultiAcrossForms(initialMultiAcross);

            if (formKeys.length > 0) {
                const tabWithData = formKeys.find(key => tabData[key].bodydata?.length > 0);
                setActiveTab(tabWithData || formKeys[0]);
            }

            setIsInitialLoad(false);
        }
    }, [tabData, isInitialLoad, isMultiSelectEnabled]);

    function getValues(data, type) {
        const valuesData: any[] = [];
        if (type === "prev") {
            data.forEach(item => {
                valuesData.push({
                    label: item.value,
                    value: item.value
                });
            });
        } else {
            data.forEach(item => {
                valuesData.push({
                    label: item,
                    value: item
                });
            });
        }
        return valuesData;
    }

    const isAlreadyOptionObject = (val: any) => {
        return (
            val &&
            typeof val === "object" &&
            typeof val.label === "string" &&
            typeof val.value === "string"
        );
    };

    const normalizeSelectedValue = (value, fieldType) => {
        if (value == null) return value;

        const needsPrev = fieldType === "dropDownPreviousResponses";

        const toOption = (val) => ({
            label: needsPrev ? val.value ?? val : val,
            value: needsPrev ? val.value ?? val : val
        });

        if (Array.isArray(value)) {
            if (value.length > 0 && isAlreadyOptionObject(value[0])) {
                return value;
            }
            return value.map(v => toOption(v));
        }

        // Single value
        if (isAlreadyOptionObject(value)) {
            return value;
        }

        return toOption(value);
    };



    function processFormFields(formFieldsData) {
        return formFieldsData.map(field => {
            const shouldTransform =
                field.field_type === "dropDown" ||
                field.field_type === "radioButton" ||
                field.field_type === "checkBox";

            const needsPrev = field.field_type === "dropDownPreviousResponses";
            const type = needsPrev ? "prev" : "";

            const safeGetValues = (values, type = "") => {
                if (!Array.isArray(values)) return values;

                if (values.length > 0 && isAlreadyOptionObject(values[0])) {
                    return values;
                }

                return getValues(values, type);
            };

            const shouldNormalizeSelected =
                shouldTransform || needsPrev;

            return {
                ...field,
                field_value:
                    shouldTransform || needsPrev
                        ? safeGetValues(field.field_value || [], type)
                        : field.field_value,
                field_selected_value: shouldNormalizeSelected
                    ? normalizeSelectedValue(
                        field.field_selected_value,
                        field.field_type
                    )
                    : field.field_selected_value
            };
        });
    }

    const initializeTabs = () => {
        const tabs: Record<string, TabDataItem> = {};
        existingData.forEach((data, index) => {
            const objectWithLargestFields = data.record_info.reduce(
                (max: any, current: any) =>
                    current.fields.length > max.fields.length ? current : max,
                data.record_info[0]
            );

            const sortedData = data.record_info.sort(
                (a: any, b: any) => b.fields.length - a.fields.length
            );

            tabs[`form-${index}`] = {
                formName: data.form_name,
                formId: data.form_id,
                headers: objectWithLargestFields,
                bodydata: sortedData,
                formIndex: index,
                filterData: [],
                originalFormData: data
            };
        });
        setTabData(tabs);
    };

    const toggle = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const handleSingleSelect = (formKey: string, value: string) => {
        setSelectedSingleRecord(value);
        setSelectedMultiAcrossForms(prev => ({
            ...prev,
            [formKey]: value
        }));
    };

    const toggleMultiWithinSelection = (formKey: string, index: number) => {
        setSelectedMultiWithinForms(prev => {
            const current = prev[formKey] || [];
            return {
                ...prev,
                [formKey]: current.includes(index)
                    ? current.filter(i => i !== index)
                    : [...current, index]
            };
        });
    };

    const getSearchText = (data: any, searchvalue: any, formKey: string) => {
        const currentFilters = tabData[formKey]?.filterData || [];
        let SearchArray = currentFilters.filter(
            (item: any) => item.field_id !== data.data.id && item.search_value !== ""
        );

        const isDateRange = typeof searchvalue === "object" && searchvalue.start !== undefined;
        const bothDatesPresent = isDateRange && searchvalue.start && searchvalue.end;
        const hasText = typeof searchvalue === "string" && searchvalue.length > 0;

        if (bothDatesPresent || hasText) {
            let Obj = {
                field_id: data.data.id,
                search_value: searchvalue,
                field_type: data.data.field_type,
                is_field: true,
                is_range: isDateRange,
                start_created_at: isDateRange && searchvalue.start
                    ? new Date(String(searchvalue.start))
                    : null,
                end_created_at: isDateRange && searchvalue.end
                    ? new Date(String(searchvalue.end))
                    : null,
                time_zone: tz,
            };
            SearchArray.push(Obj);
        } else if (isDateRange && (searchvalue.start || searchvalue.end)) {
            let Obj = {
                field_id: data.data.id,
                search_value: searchvalue.start || searchvalue.end,
                field_type: data.data.field_type,
                time_zone: tz,
            };
            SearchArray.push(Obj);
        }

        setTabData((prev) => ({
            ...prev,
            [formKey]: {
                ...prev[formKey],
                filterData: SearchArray,
            },
        }));
    };

    const applyFilter = async (type: string, formKey: string) => {
        if (type !== "text") return;

        const currentTab = tabData[formKey];
        if (!currentTab) return;

        try {
            dispatch(setLoading(true));
            const searchDataObj = [
                ...currentTab.filterData,
                ...searchIndexUidData,
            ];

            const response = await postSearchIndex(
                topicId,
                currentTab.formId,
                searchDataObj,
                true
            );

            const matchingForm = response?.data?.find((form: any) => form.form_id === currentTab.formId);
            const records = matchingForm?.record_info || [];

            setTabData((prev) => ({
                ...prev,
                [formKey]: {
                    ...prev[formKey],
                    bodydata: records,
                },
            }));

            dispatch(setLoading(false));
        } catch (e) {
            console.error("Filter error:", e);
            dispatch(setLoading(false));
        }
    };

    const handleDetailedView = (formKey: string, recordIndex: number) => {
        const form = tabData[formKey]?.bodydata[recordIndex];
        if (form) {
            setTabStates(prev => ({
                ...prev,
                [formKey]: {
                    showDetailedView: true,
                    detailedViewData: {
                        form,
                        formName: tabData[formKey].formName,
                        formKey,
                        recordIndex
                    }
                }
            }));
        }
    };

    const handleBackToList = (formKey: string) => {
        setTabStates(prev => ({
            ...prev,
            [formKey]: {
                ...prev[formKey],
                showDetailedView: false
            }
        }));
    };

    const onDetailedViewClick = () => {
        const currentSelection = selectedMultiAcrossForms[activeTab];
        if (!currentSelection) return;

        const [formKey, recordIndex] = currentSelection.split("_");
        handleDetailedView(formKey, parseInt(recordIndex));
    };

    const onSubmit = (selectionOverride?: string) => {
        const currentSelection = selectionOverride || selectedMultiAcrossForms[activeTab];
        if (!currentSelection) return;

        const [formKey, recordIndex] = currentSelection.split("_");
        const form = tabData[formKey]?.bodydata[parseInt(recordIndex)];

        const targetFormData = tabData[selectedTargetForm]?.originalFormData;
        const autoFillFields = form?.fields || [];
        const currentTab = tabData[formKey];
        const originalFormData = currentTab.originalFormData;
        if (form) {
            if (renderType === "dataEntry") {
                getExistingData({ ...form, form_name: originalFormData.form_name });
            } else {
                const uidFieldData = getSourceUidField(selectedIndexData?.original?.fields);

                const enhancedTargetFormData = {
                    ...targetFormData,
                    uidData: uidFieldData
                };

                getExistingData(enhancedTargetFormData, autoFillFields);
            }
        }
    };

    const onSubmitMultiRecord = () => {
        if (renderType === "dataEntry") {
            return;
        }

        const selectedRecords = Object.entries(selectedMultiAcrossForms);

        if (selectedRecords.length === 0) {
            showToaster(toasterTypes.WARNING, "Please select at least one record from any tab");
            return;
        }

        const autoFillFieldsArray: any[] = [];

        selectedRecords.forEach(([formKey, selectionValue]) => {
            const recordIndex = parseInt(selectionValue.split("_")[1]);
            const form = tabData[formKey]?.bodydata[recordIndex];

            if (form && form.fields) {
                autoFillFieldsArray.push({
                    formKey,
                    formName: tabData[formKey].formName,
                    formId: tabData[formKey].formId,
                    recordIndex,
                    fields: form.fields
                });
            }
        });

        if (autoFillFieldsArray.length === 0) {
            showToaster(toasterTypes.ERROR, "No valid records found for autofill");
            return;
        }

        const targetFormData = tabData[selectedTargetForm]?.originalFormData;

        if (!targetFormData) {
            showToaster(toasterTypes.ERROR, "Please select a target form");
            return;
        }

        const uidFieldData = getSourceUidField(selectedIndexData?.original?.fields);

        const enhancedTargetFormData = {
            ...targetFormData,
            uidData: uidFieldData
        };

        if (autoFillMultiRecord) {
            autoFillMultiRecord(enhancedTargetFormData, autoFillFieldsArray);
        } else {
            console.warn("autoFillMultiRecord function not provided in props");
            showToaster(toasterTypes.ERROR, "Multi-record autofill not configured");
        }
    };

    const onMultiRecordAiSummary = async () => {
        const selectedRecords: any[] = [];

        Object.entries(selectedMultiWithinForms).forEach(([formKey, indices]) => {
            indices.forEach(index => {
                const record = tabData[formKey]?.bodydata[index];
                if (record) {
                    selectedRecords.push({
                        formKey,
                        formName: tabData[formKey].formName,
                        formId: tabData[formKey].formId,
                        recordIndex: index,
                        record
                    });
                }
            });
        });

        if (selectedRecords.length === 0) {
            showToaster(toasterTypes.WARNING, "Please select at least one record");
            return;
        }

        showToaster(toasterTypes.SUCCESS, `Selected ${selectedRecords.length} record(s) for processing`);

        const filters = Object.values(
            selectedRecords.reduce((acc, item) => {
                const { formId, record } = item;

                if (!acc[formId]) {
                    acc[formId] = {
                        form_id: formId,
                        record_ids: []
                    };
                }

                acc[formId].record_ids.push(record.id);
                return acc;
            }, {} as Record<string, { form_id: string; record_ids: number[] }>)
        );

        const uidFieldData = getSourceUidField(selectedIndexData?.original.fields);

        let data: any = {
            created_on: formData.created_on,
            total_responses: formData.total_responses,
            updated_on: formData?.updated_on,
            uidData: uidFieldData
        };

        data.formsList = forms;
        data.projectData = projectData;
        let uidData = data.uidData;
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const aiAgents: any = JSON.parse(localStorage.getItem("aiAgents") || "[]");
        const aiSummaryAgent: any = aiAgents.find((agent: any) => agent.name === agentNamesDataMap.ai_summary);

        let payload = {
            "search_data": {
                "field_id": uidData?.id,
                "search_value": uidData?.field_selected_value.trim(),
                "field_type": uidData?.field_type,
                "time_zone": tz,
            },
            "filters": filters,
            "prompt": multiRecordSummaryPrompt.trim()
        }

        try {
            dispatch(setLoading(true))
            let masterAiSummaryResponse = await getMasterSummaryReport(payload, projectData.TopicID, formData.id, aiSummaryAgent?.id);
            setMultiRecordsSummaryReportData(masterAiSummaryResponse.summary);
            setAction("viewMultiRecordSummary");
            showToaster(toasterTypes.SUCCESS, 'Successfully generated ai summary');
        } catch (error) {
            showToaster(toasterTypes.ERROR, 'Unable to fetch summary data');
        } finally {
            dispatch(setLoading(false))
        }
    };


    const getSourceUidField = (fieldsData: any) => {
        const formid = formData?.id;
        let flt = fieldsData?.filter((itm: any) => itm?.field_use_type?.includes('UID')) || [];
        const sourceUidField = flt.find((itm: any) => formid === itm?.source_form_id);
        let finalUidField;
        if (sourceUidField) {
            finalUidField = sourceUidField;
        } else {
            finalUidField = flt[0];
        }
        return finalUidField;
    };

    const onAddAction = () => {
        if (renderType === "dataEntry") return;

        const currentTab = tabData[activeTab];
        if (!currentTab) return;

        const originalFormData = currentTab.originalFormData;

        const uidFieldData = getSourceUidField(selectedIndexData?.original?.fields);
        let data: any = {
            created_on: formData?.created_on,
            form_name: originalFormData.form_name,
            id: originalFormData.form_id,
            total_responses: formData?.total_responses,
            updated_on: formData?.updated_on,
            uidData: uidFieldData,
            recordId: originalFormData.record_info[0] ? originalFormData.record_info[0].id : null
        };

        data.formsList = forms;
        data.projectData = projectData;
        data.isMaster = true;
        data.masterData = existingData;

        const state = {
            data,
            parentFormData: formData,
            projectData: projectData,
            isMaster: true,
            moduleType: "dataEntry"
        };

        setCustomNavigationState(state);
        setCurrentFormKey(activeTab);
        setAction("navigateToDataEntry");
    };

    const onViewAction = async () => {
        const currentSelection = selectedMultiAcrossForms[activeTab];
        if (!currentSelection || renderType === "dataEntry") return;

        const [formKey, recordIndex] = currentSelection.split("_");

        try {
            dispatch(setLoading(true));
            const currentTab = tabData[formKey];
            const record = currentTab.bodydata[parseInt(recordIndex)];

            let response = await getFormVariables(currentTab.formId);
            if (response.data && record) {
                response.data.forEach((obj: any) => {
                    let selectedRecord = record.fields.filter((elem: any) => elem.id === obj.id);

                    if (selectedRecord.length > 0) {
                        if (obj.field_type === 'dateField') {
                            obj.selectedValue = new Date(selectedRecord[0].field_selected_value);
                        } else if (obj.field_type === 'dropDown') {
                            obj.selectedValue = {
                                label: selectedRecord[0].field_selected_value,
                                value: selectedRecord[0].field_selected_value
                            };
                        } else {
                            obj.selectedValue = selectedRecord[0].field_selected_value;
                        }
                    }
                    obj.field_category = obj.field_category || 'empty';
                });

                let temp: any = _.groupBy(response.data, "field_category");
                if (temp.empty) {
                    let data = cloneDeep(temp.empty);
                    delete temp.empty;
                    temp.empty = data;
                }

                let responseForm = await getFormData(currentTab.formId);
                setViewFormData(responseForm?.data);
                setViewFormVariables(temp);
                setSelectedRecord(record);
                setCurrentFormKey(formKey);
                setCurrentRecordIndex(parseInt(recordIndex));
                setAction('view');
            }
            dispatch(setLoading(false));
        } catch (e: any) {
            dispatch(setLoading(false));
        }
    };

    const onModifyAction = async () => {
        const currentSelection = selectedMultiAcrossForms[activeTab];
        if (!currentSelection || renderType === "dataEntry") return;

        const [formKey, recordIndex] = currentSelection.split("_");

        try {
            const currentTab = tabData[formKey];
            const record = currentTab.bodydata[parseInt(recordIndex)];

            let responseForm = await getFormData(currentTab.formId);
            if (responseForm) {
                let dataCurObj = responseForm.data;
                dataCurObj.isMaster = true;
                dataCurObj.masterData = existingData;
                dataCurObj.projectData = projectData;

                const state = {
                    data: dataCurObj,
                    projectData: projectData,
                    selectedValues: record.fields,
                    id: record.id,
                    isMaster: true,
                    moduleType: "dataEntry",
                    modifyType: "modify",
                    responseId: record.id
                };

                setCustomNavigationState(state);
                setCurrentFormKey(formKey);
                setCurrentRecordIndex(parseInt(recordIndex));
                setAction("navigateToDataEntry");
            }
        } catch (error) {
            console.error("Modify error:", error);
        }
    };

    const onDeleteAction = async () => {
        const currentSelection = selectedMultiAcrossForms[activeTab] || selectedSingleRecord;
        if (!currentSelection) return;

        const [formKey, recordIndex] = currentSelection.split("_");
        const currentTab = tabData[formKey];
        const record = currentTab.bodydata[parseInt(recordIndex)];

        try {
            dispatch(setLoading(true));
            let response = await deleteRecord(record.id);
            if (response.success) {
                showToaster(toasterTypes.SUCCESS, response.message);
                setAction("");
                if (onRefresh) {
                    onRefresh();
                } else {
                    onClose();
                }
            } else {
                showToaster(toasterTypes.ERROR, response.message);
            }
        } catch (error) {
            console.error("Delete error:", error);
            showToaster(toasterTypes.ERROR, "Failed to delete record");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const onDeleteRecordsAction = async () => {
        const hasMultiWithin = Object.values(selectedMultiWithinForms).some(arr => arr.length > 0);
        if (!hasMultiWithin) return;

        const allSelectedRecords: any[] = [];
        Object.keys(selectedMultiWithinForms).forEach(formKey => {
            const currentTab = tabData[formKey];
            selectedMultiWithinForms[formKey].forEach(recordIndex => {
                const record = currentTab.bodydata[recordIndex];
                allSelectedRecords.push({
                    id: record.id,
                    version_id: record.version_id,
                    form_id: currentTab.formId,
                    fields: record.fields
                });
            });
        });

        try {
            dispatch(setLoading(true));
            const response = await deleteRecords(allSelectedRecords);
            if (response.success) {
                showToaster(toasterTypes.SUCCESS, "Records deleted successfully");
                setAction("");
                if (onRefresh) {
                    onRefresh();
                } else {
                    onClose();
                }
            } else {
                showToaster(toasterTypes.ERROR, "Failed to delete records");
            }
        } catch (error) {
            console.error("Delete records error:", error);
            showToaster(toasterTypes.ERROR, "Failed to delete records");
        } finally {
            dispatch(setLoading(false));
        }
    };

    const dumpAction = () => {
        if (renderType === "dataEntry") return;

        const currentTab = tabData[activeTab];
        if (!currentTab) return;

        const originalFormData = currentTab.originalFormData;
        setSelectedFormData({ ...originalFormData, id: originalFormData.form_id });

        let flt = selectedIndexData?.cells?.filter((itm: any) =>
            itm.column.data?.field_use_type?.includes('UID')
        ) || [];

        if (flt.length > 0) {
            let uid_field = flt[0].column.data;
            const uidData = {
                master_uid: uid_field.field_selected_value,
                master_uid_field_id: uid_field.id
            };
            setMasterUidData(uidData);
        }

        setCurrentFormKey(activeTab);
        setAction("dump");
    };

    const onPartialModify = () => {
        const currentSelection = selectedMultiAcrossForms[activeTab];
        if (!currentSelection || renderType === "dataEntry") return;

        const [formKey, recordIndex] = currentSelection.split("_");
        const record = tabData[formKey]?.bodydata[parseInt(recordIndex)];

        if (record && formData) {
            formData.isPartialModify = true;
            const state = {
                data: formData,
                projectData: projectData,
                selectedValues: record.fields,
                id: record.id,
                moduleType: "dataEntry",
                modifyType: "modifyPartial"
            };
            setCustomNavigationState(state);
            setAction("navigateToDataEntry");
        }
    };

    const onViewRecordSummary = async () => {
        const uidFieldData = getSourceUidField(selectedIndexData?.original.fields);

        let data: any = {
            created_on: formData.created_on,
            total_responses: formData.total_responses,
            updated_on: formData?.updated_on,
            uidData: uidFieldData
        };

        data.formsList = forms;
        data.projectData = projectData;
        let uidData = data.uidData;
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const aiAgents: any = JSON.parse(localStorage.getItem("aiAgents") || "[]");
        const aiSummaryAgent: any = aiAgents.find((agent: any) => agent.name === agentNamesDataMap.ai_summary);

        let payload = {
            "search_data": {
                "field_id": uidData?.id,
                "search_value": uidData?.field_selected_value.trim(),
                "field_type": uidData?.field_type,
                "time_zone": tz,
            }
        }
        try {
            dispatch(setLoading(true))
            let masterUid = uidData?.field_selected_value.trim();
            let response = await getRecordSummary(masterUid, formData.id);
            if (response.status) {
                setAction('viewRecordSummary')
                setmasterData(response?.data?.summary)
            } else {
                let masterAiSummaryResponse = await getMasterAiSummary(payload, projectData.TopicID, formData.id, aiSummaryAgent?.id);
                showToaster(toasterTypes.SUCCESS, masterAiSummaryResponse?.message || '');
            }
        } catch (error) {
            showToaster(toasterTypes.ERROR, 'Unable to fetch summary data');
            setAction('');
        } finally {
            dispatch(setLoading(false))
        }
    }

    const onPrintReport = async () => {
        try {
            dispatch(setLoading(true))
            const topicId = projectData.TopicID;
            let response = await getPrintableTemplates(topicId);
            if (response.status) {
                setTemplateData(response?.data);
                const templateValues = response?.data.map((template) => ({ label: template.title, value: template.id }))
                setPrintableTemplateValues(templateValues);
                setAction('printableTemplate')
            }
        } catch (error) {
            showToaster(toasterTypes.ERROR, 'Unable to fetch report templates');
            setAction('');
        } finally {
            dispatch(setLoading(false))
        }
    }

    const shouldShowActionButtons = () => {
        if (renderType === "dataEntry") return false;

        const currentTab = tabData[activeTab];
        if (!currentTab) return false;

        const originalFormData = currentTab.originalFormData;

        if (selectedIndexData?.checkMandatory) {
            return originalFormData?.form_use_category &&
                originalFormData.form_use_category.includes('MANDATORY');
        }

        return true;
    };

    const proceedMultiRecordReportType = () => {
        if (multiRecordReportType === "aiSummary") {
            onMultiRecordAiSummary();
        }
        // else if(multiRecordReportType === "printable"){
        //     setAction("printableTemplate")
        // }
    }

    const getSelectedTemplateFilters = async () => {
        try {
            dispatch(setLoading(true));
            const templateId: any = printableTemplate?.value;
            let response = await getTemplateFilters(templateId);
            const selectedTemplate = templateData?.find(template => template.id === printableTemplate?.value)

            if (response.status && response.data) {
                const groupedFilters = groupFiltersByForm(response.data, selectedTemplate.forms);
                setTemplateFilters(groupedFilters);
                console.log(groupedFilters)

                const initialValues: Record<string, any> = {};
                response.data.forEach((field: any) => {
                    initialValues[field.id] = field.field_selected_value || '';
                });
                setFilterValues(initialValues);
            } else if (response.status && !response.data) {
                generatePrintableReport()
            }
        } catch (error) {
            showToaster(toasterTypes.ERROR, 'Unable to fetch templates filters');
            setAction('');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const groupFiltersByForm = (filters: any[], forms: any[]) => {
        const usedFieldIds = new Set<string>();
        const groupedData: any[] = [];

        forms.forEach((form) => {
            const formFilters: any[] = [];

            filters.forEach((field) => {
                // Skip if field is already used
                if (usedFieldIds.has(field.id)) return;

                // Check if field belongs to this form
                const belongsToForm =
                    field.form_id === form.form_id ||
                    (field.field_visibility && field.field_visibility.includes(form.form_id));

                if (belongsToForm) {
                    formFilters.push(field);
                    usedFieldIds.add(field.id);
                }
            });

            const processedFormFilters = processFormFields(formFilters);

            // Only add form if it has filters
            if (formFilters.length > 0) {
                groupedData.push({
                    form_id: form.form_id,
                    section_title: form.section_title,
                    filters: processedFormFilters
                });
            }
        });

        return groupedData;
    };

    function onValueChange(name, value, values, fieldValues = null) {
        const fieldId = name;

        setTemplateFilters(prevFilters =>
            prevFilters.map(filterGroup => ({
                ...filterGroup,
                filters: filterGroup.filters.map(filter =>
                    filter.id === fieldId
                        ? { ...filter, field_selected_value: value }
                        : filter
                )
            }))
        );

        setFilterValues(prev => ({
            ...prev,
            [fieldId]: value
        }));
    }

    const formatFilterFieldValue = (field: any, value: any) => {
        if (value === null || value === undefined) {
            return null;
        }

        // Handle grid fields
        if (gridFields.includes(field.field_type)) {
            return Array.isArray(value) ? value : [];
        }

        switch (field.field_type) {
            case "dateField":
            case "dateTime":
                return value ? dayjs(value).toISOString() : null;

            case "dropDown":
                // { label, value } OR primitive
                if (typeof value === "object" && value !== null) {
                    return value.value;
                }
                return value;

            case "radioButton":
                return value;

            case "checkBox":
                return Array.isArray(value) ? value : [];

            case "textBoxGrid":
            case "checkBoxGrid":
            case "radioButtonGrid":
                return Array.isArray(value) ? value : [];

            default:
                return value;
        }
    };


    const generatePrintableReport = async () => {
        const selectedTemplate = templateData?.find(template => template.id === printableTemplate?.value)
        try {
            dispatch(setLoading(true));

            const filtersPayload = templateFilters?.map((formGroup: any) => {
                const fields = formGroup.filters
                    .map((field: any) => {
                        const rawValue = filterValues[field.id];
                        const formattedValue = formatFilterFieldValue(field, rawValue);

                        const isEmpty =
                            formattedValue === null ||
                            formattedValue === '' ||
                            (Array.isArray(formattedValue) && formattedValue.length === 0);

                        if (isEmpty) return null;

                        return {
                            id: field.id,
                            field_selected_value: formattedValue,
                            field_type: field.field_type,
                            field_visibility: field.field_visibility || []
                        };
                    })
                    .filter(Boolean);

                return {
                    form_id: formGroup.form_id,
                    fields: fields || [],
                    time_zone: tz
                };
            });

            const normalizedFiltersPayload = selectedTemplate.forms.map(
                (templateForm: any) => {
                    const existingForm = filtersPayload.find(
                        (f: any) => f.form_id === templateForm.form_id
                    );

                    if (existingForm) {
                        return existingForm;
                    }

                    return {
                        form_id: templateForm.form_id,
                        fields: [],
                        time_zone: tz
                    };
                }
            );

            const payload = {
                "search_data": searchIndexUidData,
                "template_filter_data": normalizedFiltersPayload
            }

            const response = await getTemplateRecords(payload);
            const templateSelected = templateData?.find(
                (template: any) => template.id === printableTemplate?.value
            );
            setSelectedTemplateData(templateSelected);
            setReportData(response.data);
            setAction('generateReport');
            showToaster(toasterTypes.SUCCESS, 'Report generated successfully');

        } catch (error) {
            console.log(error)
            showToaster(toasterTypes.ERROR, 'Unable to generate report');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const isSingleMode = selectionMode === "single";
    const isMultiWithinMode = selectionMode === "multiWithinForms";
    const isMultiAcrossMode = selectionMode === "multiAcrossForms";

    // Check if current tab has a selection
    const hasCurrentTabSelection = selectedMultiAcrossForms[activeTab] !== undefined;

    const renderDetailedView = (formKey: string) => {
        const currentTabState = tabStates[formKey];
        if (!currentTabState?.detailedViewData) return null;

        const { form, formName, recordIndex } = currentTabState.detailedViewData;
        const showActions = shouldShowActionButtons();

        return (
            <div className="detailed-view-container">
                <div className="detailed-view-header">
                    <h3>{formName} - Detailed View</h3>
                </div>
                <div className="detailed-view-content">
                    {form?.fields?.map((field: any, index: number) => (
                        <div key={`detail-field-${index}`} className="detail-field-row">
                            <div className="detail-field-label">
                                <strong>{field.field_name}:</strong>
                            </div>
                            <div className="detail-field-value">
                                {(() => {
                                    if (field?.field_type === "dateField") {
                                        return field?.field_selected_value?.length > 0
                                            ? convertToIST(field.field_selected_value)
                                            : "-";
                                    } else if (field?.field_type === "checkBox") {
                                        return Array.isArray(field.field_selected_value)
                                            ? field.field_selected_value
                                                .map((itm: any) =>
                                                    typeof itm === "object"
                                                        ? itm.value || itm.label || JSON.stringify(itm)
                                                        : itm
                                                )
                                                .join(", ")
                                            : "-";
                                    } else {
                                        const value = field?.field_selected_value;
                                        if (value == null) return "-";
                                        if (typeof value === "object") {
                                            return (
                                                value.value ||
                                                value.label ||
                                                value.name ||
                                                JSON.stringify(value)
                                            );
                                        }
                                        return String(value);
                                    }
                                })()}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="detailed-view-footer">
                    <CustomButton
                        type="primary-alt"
                        className="mr-2"
                        onClick={() => handleBackToList(formKey)}
                        text="Back"
                    />
                    {showActions && (
                        <>
                            <CustomButton
                                type="primary"
                                className="ml-2"
                                onClick={onAddAction}
                                text="Add"
                            />
                            <CustomButton
                                type="primary"
                                className="ml-2"
                                onClick={async () => {
                                    setSelectedSingleRecord(`${formKey}_${recordIndex}`);
                                    await onViewAction();
                                }}
                                text="View"
                            />
                            <CustomButton
                                type="primary"
                                className="ml-2"
                                onClick={async () => {
                                    setSelectedSingleRecord(`${formKey}_${recordIndex}`);
                                    await onModifyAction();
                                }}
                                text="Edit"
                            />
                            <CustomButton
                                type="primary"
                                className="ml-2"
                                onClick={() => {
                                    setSelectedSingleRecord(`${formKey}_${recordIndex}`);
                                    setAction('delete');
                                }}
                                text="Delete"
                            />
                            <CustomButton
                                type="primary"
                                className="ml-2"
                                onClick={() => {
                                    setSelectedSingleRecord(`${formKey}_${recordIndex}`);
                                    dumpAction();
                                }}
                                text="Dump"
                            />
                        </>
                    )}
                    <CustomButton
                        type="primary"
                        className="ml-2"
                        onClick={() => {
                            const selectionValue = `${formKey}_${recordIndex}`;
                            setSelectedSingleRecord(selectionValue);
                            setSelectedMultiAcrossForms(prev => ({
                                ...prev,
                                [formKey]: selectionValue
                            }));
                            renderType === "dataEntry" ? onSubmit(selectionValue) : setAction("targetFormModal");
                        }}
                        text="Auto-fill"
                    />
                </div>
            </div>
        );
    };

    const renderFormTable = (formKey: string) => {
        const data = tabData[formKey];
        if (!data) return null;

        const currentTabState = tabStates[formKey];
        if (currentTabState?.showDetailedView) {
            return renderDetailedView(formKey);
        }

        const isRowSelected = !isMultiSelectEnabled && selectedMultiAcrossForms[formKey];
        const showActions = shouldShowActionButtons();
        const hasMultiWithinSelection = selectedMultiWithinForms[formKey]?.length > 0;
        const hasMultiAcrossSelection = Object.keys(selectedMultiAcrossForms).length > 0;

        return (
            <div className="search-index-table-container">
                <div className="form-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>{data.formName}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {!isMultiSelectEnabled && (
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                Single Select Mode
                            </span>
                        )}
                        {isMultiSelectEnabled && (
                            <span style={{ fontSize: '12px', color: '#28a745', fontWeight: 'bold' }}>
                                Multi Select Mode - {selectedMultiWithinForms[formKey]?.length || 0} selected
                            </span>
                        )}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '14px' }}>
                            <input
                                type="checkbox"
                                checked={isMultiSelectEnabled}
                                onChange={(e) => setIsMultiSelectEnabled(e.target.checked)}
                            />
                            Multi Select
                        </label>
                    </div>
                </div>
                {isMultiSelectEnabled && (
                    <div style={{
                        padding: '10px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '4px',
                        marginBottom: '10px',
                        fontSize: '13px'
                    }}>
                        Multi-select mode enabled. Single-record actions (View, Edit, Detailed View) are disabled.
                    </div>
                )}
                <div className="table-wrapper">
                    <table className="search-index-table">
                        <thead>
                            <tr>
                                <th className="select-column first-row-col">
                                    {isMultiSelectEnabled ? "✓" : "#"}
                                </th>
                                {data.headers?.fields?.map((item: any, i: number) => (
                                    <th key={`header-${formKey}-${i}`} title={item.field_name}>
                                        <div className="header-content">
                                            <span>{item.field_name}</span>
                                            <ColumnFilter
                                                getSearchText={(d: any, v: any) => getSearchText(d, v, formKey)}
                                                applyFilter={(type: string) => applyFilter(type, formKey)}
                                                column={{
                                                    field_name: item.field_name,
                                                    field_type: item.field_type,
                                                    id: item.id || item.field_name,
                                                    data: item,
                                                }}
                                                previousData={data.filterData}
                                            />
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.bodydata && data.bodydata.length > 0 ? (
                                data.bodydata.map((itemd: any, ind: number) => (
                                    <tr key={`row-${formKey}-${ind}`}>
                                        <td className="select-column">
                                            {isMultiSelectEnabled ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedMultiWithinForms[formKey]?.includes(ind) || false}
                                                    onChange={() => toggleMultiWithinSelection(formKey, ind)}
                                                />
                                            ) : (
                                                <FieldItem
                                                    name={`form-${formKey}`}
                                                    value={selectedMultiAcrossForms[formKey] || ""}
                                                    values={[
                                                        { label: "", value: `${formKey}_${ind}` },
                                                    ]}
                                                    type={FIELD_TYPES.RADIO_BUTTON}
                                                    onChange={(val: any) => handleSingleSelect(formKey, val)}
                                                />
                                            )}
                                        </td>
                                        {data.headers?.fields?.map((headerField: any, tabindex: number) => {
                                            const matchingField = itemd?.fields?.find(
                                                (field: any) =>
                                                    field.id === headerField.id ||
                                                    field.field_id === headerField.id ||
                                                    field.field_name === headerField.field_name
                                            );

                                            return (
                                                <td key={`cell-${formKey}-${ind}-${tabindex}`}>
                                                    {matchingField ? (
                                                        (() => {
                                                            if (matchingField?.field_type === "dateField") {
                                                                return matchingField?.field_selected_value?.length > 0
                                                                    ? convertToIST(matchingField.field_selected_value)
                                                                    : "-";
                                                            } else if (matchingField?.field_type === "checkBox") {
                                                                return Array.isArray(matchingField.field_selected_value)
                                                                    ? matchingField.field_selected_value
                                                                        .map((itm: any) =>
                                                                            typeof itm === "object"
                                                                                ? itm.value || itm.label || JSON.stringify(itm)
                                                                                : itm
                                                                        )
                                                                        .join(", ")
                                                                    : "-";
                                                            } else {
                                                                const value = matchingField?.field_selected_value;
                                                                if (value == null) return "-";
                                                                if (typeof value === "object") {
                                                                    return (
                                                                        value.value ||
                                                                        value.label ||
                                                                        value.name ||
                                                                        JSON.stringify(value)
                                                                    );
                                                                }
                                                                return String(value);
                                                            }
                                                        })()
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={data?.headers?.fields?.length + 1}
                                        className="no-records"
                                    >
                                        No records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="form-footer">
                    <CustomButton
                        type="primary-alt"
                        className="mr-2"
                        onClick={onClose}
                        text="Cancel"
                    />
                    <CustomButton
                        type="primary"
                        onClick={onDetailedViewClick}
                        text="Detailed View"
                        isDisabled={isMultiSelectEnabled || !isRowSelected}
                    />
                    {showActions && (
                        <>
                            <CustomButton
                                type="primary"
                                className="ml-2"
                                onClick={onAddAction}
                                text="Add"
                                isDisabled={isMultiSelectEnabled}
                            />
                            {data.bodydata && data.bodydata.length > 0 && (
                                <>
                                    <CustomButton
                                        type="primary"
                                        className="ml-2"
                                        onClick={onViewAction}
                                        text="View"
                                        isDisabled={isMultiSelectEnabled || !isRowSelected}
                                    />
                                    <CustomButton
                                        type="primary"
                                        className="ml-2"
                                        onClick={onModifyAction}
                                        text="Edit"
                                        isDisabled={isMultiSelectEnabled || !isRowSelected}
                                    />
                                    <CustomButton
                                        type="primary"
                                        className="ml-2"
                                        onClick={() => setAction('delete')}
                                        text="Delete"
                                        isDisabled={isMultiSelectEnabled || !isRowSelected}
                                    />
                                </>
                            )}
                            <CustomButton
                                type="primary"
                                className="ml-2"
                                onClick={dumpAction}
                                text="Dump"
                                isDisabled={isMultiSelectEnabled}
                            />
                        </>
                    )}
                    <CustomButton
                        type="primary"
                        onClick={() => renderType === "dataEntry" ? onSubmit() : setAction("targetFormModal")}
                        text="Auto-fill"
                        isDisabled={isMultiSelectEnabled || !isRowSelected}
                    />
                    <CustomButton
                        type="primary"
                        className="ml-2"
                        onClick={() => renderType === "dataEntry" ? onSubmitMultiRecord() : setAction("multiRecordTargetFormModal")}
                        text="Multi Record Auto-fill"
                        isDisabled={isMultiSelectEnabled || !hasMultiAcrossSelection}
                    />
                    <CustomButton
                        type="primary"
                        onClick={() => setAction("multiRecordReport")}
                        text="Multi Record Actions"
                        isDisabled={!isMultiWithinMode || !hasMultiWithinSelection}
                    />
                    <CustomButton
                        type="primary"
                        className="ml-2"
                        onClick={() => setAction('deleteRecords')}
                        text="Delete Selected"
                        isDisabled={!isMultiWithinMode || !hasMultiWithinSelection}
                    />
                </div>
            </div>
        );
    };

    // Render action modals/components
    if (action === 'view' && viewFormData && viewFormVariables) {
        return (
            <ViewForm
                projectData={projectData}
                selectedRecord={selectedRecord}
                variables={viewFormVariables}
                formData={viewFormData}
                modify={() => onModifyAction()}
                partialModify={onPartialModify}
                permissionData={permissionData}
                onClose={() => setAction('')}
            />
        );
    }

    if (action === "navigateToDataEntry" && customNavigationState) {
        return (
            <SurveyFormComponent
                {...customNavigationState}
                onClose={() => setAction("")}
            />
        );
    }

    if (action === "dump" && selectedFormData && masterUidData) {
        return (
            <Dump
                masterUidData={masterUidData}
                formData={selectedFormData}
                projectData={projectData}
                onClose={() => setAction("")}
            />
        );
    }

    if (action === "viewRecordSummary") {
        return <ViewRecordSummary projectData={projectData} mdata={masterData} onClose={() => setAction("")} id={formData.id} />
    }

    if (action === "generateReport") {
        return <GenerateReport
            projectData={projectData}
            reportData={reportData}
            filterData={filterValues}
            templateData={selectedTemplateData}
            onClose={() => setAction("")}
        />
    }

    if (action === "viewMultiRecordSummary" && multiRecordsSummaryReportData) {
        return (
            <ViewRecordSummary mdata={multiRecordsSummaryReportData} projectData={projectData} onClose={() => setAction("")} />
        )
    }

    return (
        <>
            {
                action === 'delete' && (
                    <ModalPopup
                        modalType='delete'
                        modalText={(() => {
                            const selection = selectedMultiAcrossForms[activeTab] || selectedSingleRecord;
                            if (!selection) return "";
                            const [formKey, index] = selection.split("_");
                            const record = tabData[formKey].bodydata[parseInt(index)];
                            return record.fields.map((item: any) => item.field_selected_value).join("   ");
                        })()}
                        actionText="Are you sure, you want to delete? This action cannot be undone"
                        closeModal={() => { setAction(''); }}
                        onAction={() => onDeleteAction()}
                    />
                )
            }
            {
                action === 'deleteRecords' &&
                <ModalPopup
                    modalType='delete'
                    actionText="Are you sure, you want to delete these records? This action cannot be undone"
                    closeModal={() => { setAction(''); }}
                    onAction={() => onDeleteRecordsAction()}
                />
            }
            {action === "targetFormModal" && (
                <ModalComponent
                    width={window.innerWidth > 700 ? window.innerWidth / 2 : window.innerWidth}
                    isOpen={true}
                    onClose={() => setAction("")}
                    header={"Target Form Selection"}
                    centered={true}
                    customClass="target-form-selection-modal"
                    body={
                        <>
                            <div className="target-forms-container">
                                <FieldItem
                                    name="targetForm"
                                    value={selectedTargetForm}
                                    values={Object.keys(tabData).map((formKey) => ({
                                        label: tabData[formKey].formName,
                                        value: formKey
                                    }))}
                                    type={FIELD_TYPES.RADIO_BUTTON}
                                    onChange={(val: any) => setSelectedTargetForm(val)}
                                />
                            </div>
                        </>
                    }
                    footer={<>
                        <CustomButton
                            type="primary"
                            onClick={() => {
                                onSubmit();
                            }}
                            text="Continue"
                        />
                    </>}
                />
            )}

            {action === "printableTemplate" && (
                <ModalComponent
                    width={window.innerWidth > 700 ? window.innerWidth / 2 : window.innerWidth}
                    isOpen={true}
                    onClose={() => {
                        setAction("");
                        setTemplateFilters([]);
                        setFilterValues({});
                        setPrintableTemplate(null);
                    }}
                    header={templateFilters.length > 0 ? "Select Filters" : "Printable Template"}
                    centered={true}
                    customClass="printable-template-modal"
                    body={
                        <>
                            {templateFilters.length === 0 ? (
                                <div className="printable-template-container">
                                    <Col>
                                        <FieldItem
                                            {...printableTemplateElements.printableTemplate}
                                            value={printableTemplate}
                                            values={printableTemplateValues}
                                            onChange={(val: any) => setPrintableTemplate(val)}
                                        />
                                    </Col>
                                </div>
                            ) : (
                                <div className="template-filters-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    {templateFilters.map((formGroup: any, groupIndex: number) => (
                                        <div key={formGroup.form_id} style={{ marginBottom: '30px' }}>
                                            <h4 style={{
                                                marginBottom: '15px',
                                                paddingBottom: '8px',
                                                borderBottom: '2px solid #e0e0e0',
                                                color: '#333',
                                                fontWeight: '600'
                                            }}>
                                                {formGroup.section_title}
                                            </h4>
                                            {formGroup.filters.map((field: any, index: number) => (
                                                <Col key={field.id || index} style={{ marginBottom: '15px' }}>
                                                    <FieldItem
                                                        name={field.id}
                                                        placeholder={
                                                            field.field_type === "number" ||
                                                                field.field_type === "textBox" ||
                                                                field.field_type === "textArea"
                                                                ? "Enter"
                                                                : "Select"
                                                        }
                                                        label={field?.field_name}
                                                        type={
                                                            field.field_type === "dateField" ? FIELD_TYPES.DATEPICKER_NEW
                                                                : field.field_type === "dropDown" ? FIELD_TYPES.DROP_DOWN
                                                                    : field.field_type === "boolean" ? FIELD_TYPES.BOOLEAN
                                                                        : field.field_type === "radioButton" ? FIELD_TYPES.RADIO_BUTTON
                                                                            : field.field_type === "checkBox" ? FIELD_TYPES.CHECK_BOX
                                                                                : field.field_type === "textArea" ? FIELD_TYPES.TEXT_AREA
                                                                                    : field.field_type === "number" ? FIELD_TYPES.TEXT
                                                                                        : FIELD_TYPES.TEXT
                                                        }
                                                        isNumber={field.field_type === "number"}
                                                        value={field.field_type === "instruction"
                                                            ? field.field_name
                                                            : field.field_selected_value}
                                                        values={field.field_value}
                                                        rowValues={field.field_row_value}
                                                        isListed={true}
                                                        onChange={(...e) => {
                                                            handleChange(field.id, ...e);
                                                        }}
                                                    />
                                                </Col>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    }
                    footer={
                        <>
                            <CustomButton
                                type="primary"
                                onClick={() => {
                                    if (templateFilters.length === 0) {
                                        getSelectedTemplateFilters();
                                    } else {
                                        generatePrintableReport();
                                    }
                                }}
                                text="Continue"
                                isDisabled={!printableTemplate}
                            />
                        </>
                    }
                />
            )}

            {action === "multiRecordReport" && (
                <ModalComponent
                    width={window.innerWidth > 700 ? window.innerWidth / 2 : window.innerWidth}
                    isOpen={true}
                    onClose={() => setAction("")}
                    header={"Type of Multi-Record Report"}
                    centered={true}
                    customClass="multi-record-report-modal"
                    body={
                        <>
                            <div className="multi-record-report-container">
                                <FieldItem
                                    {...multiRecordReportElements.report_type}
                                    value={multiRecordReportType}
                                    onChange={(val: any) => setMultiRecordReportType(val)}
                                />
                            </div>
                        </>
                    }
                    footer={<>
                        <CustomButton
                            type="primary"
                            onClick={() => {
                                setAction("multiRecordSummaryPrompt");
                            }}
                            text="Continue"
                        />
                    </>}
                />
            )}

            {action === "multiRecordSummaryPrompt" && (
                <ModalComponent
                    width={window.innerWidth > 700 ? window.innerWidth / 2 : window.innerWidth}
                    isOpen={true}
                    onClose={() => setAction("")}
                    header={"Multi-Record Summary Prompt"}
                    centered={true}
                    customClass="multi-record-report-modal"
                    body={
                        <>
                            <div className="multi-record-report-container">
                                <FieldItem
                                    {...multiRecordReportElements.prompt}
                                    value={multiRecordSummaryPrompt}
                                    onChange={(val: any) => setMultiRecordSummaryPrompt(val)}
                                />
                            </div>
                        </>
                    }
                    footer={<>
                        <CustomButton
                            type="primary"
                            onClick={() => {
                                proceedMultiRecordReportType();
                            }}
                            text="Continue"
                        />
                    </>}
                />
            )}

            {
                action === "viewMultiRecordSummary" && multiRecordsSummaryReportData && (
                    <ViewRecordSummary mdata={multiRecordsSummaryReportData} projectData={projectData} onClose={() => setAction("")} />
                )
            }

            {renderType !== "dataEntry" && (
                <div className="top-bar search-index-top-bar">
                    <div className={window.innerWidth > 750 ? "page-header-style" : "page-header-data-style"}>
                        <div className="title-container">
                            <div className={window.innerWidth > 750 ? "project-title" : "project-title-mob"}>
                                <ArrowLeftOutlined
                                    style={{ color: "#fff", fontSize: "36px", margin: "0px 10px" }}
                                    onClick={onClose}
                                />
                                <div className="title">
                                    {projectData?.TopicDesc || projectData?.topicdesc}
                                </div>
                            </div>

                            <p className="master-uid">
                                Master UID : {searchIndexUidData[0]?.search_value}
                            </p>
                        </div>

                        <div className="action-buttons">
                            <CustomButton
                                type="secondary"
                                onClick={
                                    // () => setAction("generateReport")
                                    onPrintReport
                                }
                                text="Print Report"
                            />
                            <CustomButton
                                type="secondary"
                                onClick={onViewRecordSummary}
                                text="View Ai Summary"
                            />
                        </div>
                    </div>

                </div>
            )}
            <div className="search-index-tabs-container">
                <Nav tabs className="search-index-nav-tabs">
                    {renderType === "dataEntry" && (
                        <NavItem className="data-entry-nav-link">
                            <NavLink
                                className={classnames({ active: activeTab === "data-entry" })}
                                onClick={() => toggle("data-entry")}
                            >
                                Data Entry
                            </NavLink>
                        </NavItem>
                    )}
                    {Object.keys(tabData).map((formKey) => (
                        <NavItem key={formKey}>
                            <NavLink
                                className={classnames({ active: activeTab === formKey })}
                                onClick={() => toggle(formKey)}
                            >
                                {tabData[formKey].formName} ({tabData[formKey].bodydata?.length || 0})
                                {selectedMultiAcrossForms[formKey] && !isMultiSelectEnabled && (
                                    <span style={{ marginLeft: '5px', color: '#28a745' }}>✓</span>
                                )}
                                {selectedMultiWithinForms[formKey]?.length > 0 && (
                                    <span style={{
                                        marginLeft: '5px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        borderRadius: '10px',
                                        padding: '2px 6px',
                                        fontSize: '11px'
                                    }}>
                                        {selectedMultiWithinForms[formKey].length}
                                    </span>
                                )}
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>

                <TabContent activeTab={activeTab} className="search-index-tab-content">
                    {renderType === "dataEntry" && (
                        <TabPane tabId="data-entry">
                            <div className="data-entry-tab">{renderDataEntryForm()}</div>
                        </TabPane>
                    )}

                    {Object.keys(tabData).map((formKey) => (
                        <TabPane key={formKey} tabId={formKey}>
                            {renderFormTable(formKey)}
                        </TabPane>
                    ))}
                </TabContent>
            </div>
        </>
    );
};

export default SearchIndexTabs;